import { Dayjs } from 'dayjs';

import { FileReference, SDate } from '@summit/core';

import { IUserExperience, SummitFormattedText } from '@summit/models';
import { EboTenantItemTo } from '../base-classes/ebo-tenant-item-to';
import { CalendarEvent } from '../calendar/calendar-event';
import { Employee } from '../employee/employee';
import { UserExperience } from '../shared/user-experience';
import { Truck } from '../truck/truck';
import { EProcessStatus, EProcessStatusActiveFinal, EProcessStatusFinal } from './e-process-status.enum';
import { EProcessEmployeeRelatedTypes, EProcessType } from './e-process-type.enum';
import { ETask } from './e-task';

export class EProcess extends EboTenantItemTo {
  type: EProcessType;
  status: EProcessStatus;
  dueDateTime: Dayjs;
  description?: string;

  employeeID?: string;
  employee?: Employee;

  comment?: string;

  truckId?: string;
  truck?: Truck;

  createdOn: Dayjs;
  createdById: string;
  createdBy?: Employee;

  tasks: ETask[];
  //Reference Objects
  calendarEvents?: CalendarEvent[];
  files?: FileReference[];

  constructor(jsonData?: Partial<EProcess>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.dueDateTime) this.dueDateTime = SDate.newUtc(jsonData.dueDateTime);
      if (jsonData.employee) this.employee = new Employee(jsonData.employee);
      if (jsonData.createdOn) this.createdOn = SDate.newUtc(jsonData.createdOn);
      if (jsonData.createdById) this.createdById = jsonData.createdById;
      if (jsonData.createdBy) this.createdBy = new Employee(jsonData.createdBy);
      if (jsonData.tasks) this.tasks = jsonData.tasks.map((t) => new ETask(t));
      if (jsonData.truck) this.truck = new Truck(jsonData.truck);
      if (jsonData.calendarEvents)
        this.calendarEvents = jsonData.calendarEvents.map((event) => new CalendarEvent(event));
      if (jsonData.files) this.files = jsonData.files.map((file) => new FileReference(file));
    }
    this.status ??= EProcessStatus.Pending;
    this.type ??= EProcessType.Unknown;
    this.calendarEvents ??= [];
    this.files ??= [];
  }

  toShortString(): string {
    return `${this.name} (${this.status})`;
  }

  getTasksByPath(): { path: number; steps: { step: number; task: ETask }[] }[] {
    const response = [];
    const paths =
      this.tasks
        ?.map((t) => t.path)
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort() ?? [];
    paths.forEach((path) => {
      const pathObject = {
        path: path,
        steps: []
      };
      const pathTasks = this.tasks.filter((t) => t.path === path).sort((a, b) => a.cycle - b.cycle);
      const steps = pathTasks.map((t) => t.cycle).filter((value, index, self) => self.indexOf(value) === index);
      steps.forEach((step) => {
        pathObject.steps.push({
          step: step,
          task: pathTasks.filter((t) => t.cycle === step)[0]
        });
      });
      response.push(pathObject);
    });
    return response;
  }

  private _activeTasks: ETask[];
  getNextTasks(): ETask[] {
    if (this._activeTasks) return this._activeTasks;
    this._activeTasks = [];
    const paths = this.getTasksByPath();
    //console.log('EProcess.getNextTasks', paths);
    paths.forEach((path) => {
      const nextStep = path.steps.find((s) => s.task.status === EProcessStatus.Pending);
      if (nextStep) this._activeTasks.push(nextStep.task);
    });
    return this._activeTasks;
  }

  isActiveTask(task: ETask): boolean {
    const activeTasks = this.getNextTasks();
    return activeTasks.find((t) => t.id === task.id) !== undefined;
  }

  isApprovable(ux: UserExperience): boolean {
    if (this.status !== EProcessStatus.Pending) return false;
    if (ux.isAdmin()) return true;

    const activeTasks = this.getNextTasks();
    if (ux.canEditTimeOff() && activeTasks.length === 0) return true; //new Entry
    const approvalTask = activeTasks.find((task) => task.isApprovable(ux));
    console.log('EProcess.isApprovable returning', approvalTask, approvalTask !== undefined);
    return approvalTask !== undefined;
  }

  isTooClose(ux: IUserExperience): boolean {
    return EProcessEmployeeRelatedTypes.includes(this.type) && this.employeeID == ux.activeEmployeeID;
  }

  isUpdatable(ux: UserExperience): boolean {
    if (ux.isAdmin()) return true;
    if (ux.canEditTimeOff()) return this.employeeID !== ux.activeEmployeeID;
    return false;
  }

  isCancelable(ux: UserExperience): boolean {
    if (ux.isAdmin()) return true;
    if (ux.canEditTimeOff() && EProcessStatusActiveFinal.includes(this.status)) return true;
    if (
      !EProcessStatusFinal.includes(this.status) &&
      (this.createdById == ux.activeEmployeeID ||
        this.employeeID == ux.activeEmployeeID ||
        this.employee?.delegateID == ux.activeEmployeeID)
    )
      return true;
    return false;
  }

  get assigneeSummary(): SummitFormattedText {
    const response = new SummitFormattedText();
    const activeTasks = this.getNextTasks();
    if (activeTasks.length > 0) {
      activeTasks.forEach((task) => {
        if (activeTasks.length > 1)
          response.AddLine(`Path ${task.path} Step ${task.cycle}`, [
            'flex',
            'flex-row',
            'justify-center',
            'bold',
            'underline'
          ]);
        response.AddText(task.assigneeSummary);
      });
    }
    return response.lines.length > 0 ? response : null;
  }
  set assigneeSummary(value: SummitFormattedText) {
    //nop
  }
}
